import type { ReactNode } from 'react';

export type BaseSelectOption<Value = string> = {
  value: Value;
  label: ReactNode;
};

/**
 * Finds the option whose value matches the supplied value.
 *
 * @param options Options to search.
 * @param value Value to match.
 * @returns The matching option, or null when no option matches.
 */
export const findSelectOption = <Option extends { value?: unknown }>(
  options: Option[] = [],
  value?: unknown,
): Option | null => {
  return options.find((option) => option.value?.toString() === value?.toString()) ?? null;
};

/**
 * Normalizes a single or multi-select value into an array.
 *
 * @param values Select value or values to normalize.
 * @returns The supplied values as an array, excluding only nullish values.
 */
export const normalizeSelectValues = <Value>(values?: Value | Value[] | null): Value[] => {
  if (values === null || values === undefined) return [];
  return Array.isArray(values) ? values : [values];
};

/**
 * Finds every option whose value matches one of the supplied values.
 *
 * @param options Options to search.
 * @param values Values to match.
 * @returns Matching options in their original option-list order.
 */
export const findSelectOptions = <Option extends { value?: unknown }>(
  options: Option[] = [],
  values?: unknown | unknown[] | null,
): Option[] => {
  const selectedValues = new Set(normalizeSelectValues(values).map((value) => value?.toString()));

  return options.filter((option) => selectedValues.has(option.value?.toString()));
};

/**
 * Converts values into options for BaseSelect.
 *
 * @param values Values to convert.
 * @param getLabel Function that creates each option label.
 * @returns Options containing each value and its label.
 */
export const buildSelectOptions = <Value extends string | number>(
  values: Value[] = [],
  getLabel: (value: Value) => ReactNode = (value) => value,
): BaseSelectOption<Value>[] =>
  values.map((value) => ({
    value,
    label: getLabel(value),
  }));
